import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { formatDuration } from '../../../utils/time';
import { formatTimeAgo } from '../../../utils/date';
import { normalizeLanguageCode } from '../../../utils/platform';
import type { BaseMediaThumbnailProps } from '../../media/MediaThumbnail';
import Badge, { BadgeType } from '../../common/Badge';
import HoverPlayButton from '../../common/HoverPlayButton';
import CountdownTimer from '../../common/CountdownTimer';

type BroadcastStatus = 'live' | 'upcoming' | 'none' | 'completed';

interface VideoThumbnailProps extends Omit<BaseMediaThumbnailProps, 'aspectRatio' | 'navigationPath'> {
  views?: number;
  broadcastStatus?: BroadcastStatus;
  scheduledStartTime?: string;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  id,
  title,
  thumbnail,
  duration,
  createdAt,
  views,
  broadcastStatus = 'none',
  scheduledStartTime,
  className = '',
}) => {
  const { isDark } = useTheme();
  const { isRTL } = useDirection();
  const { t, i18n } = useTranslation();

  const isNew = () => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    return diffHours <= 24;
  };

  const getBadgeType = (): BadgeType | null => {
    switch (broadcastStatus) {
      case 'live':
        return 'live';
      case 'upcoming':
        return 'upcoming';
      case 'none':
      case 'completed':
        if (isNew()) {
          return 'new';
        }
        return null;
      default:
        return null;
    }
  };

  const getTimeDisplay = useMemo(() => {
    if (broadcastStatus !== 'upcoming' || !scheduledStartTime) return null;

    const startTime = new Date(scheduledStartTime);
    const now = new Date();
    const diffHours = (startTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (diffHours <= 24) {
      return { type: 'countdown' as const };
    }

    return {
      type: 'date' as const,
      value: startTime.toLocaleString(i18n.language, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      })
    };
  }, [scheduledStartTime, broadcastStatus, i18n.language]);

  const badgeType = getBadgeType();

  return (
    <div className={`group relative w-full ${className} ${isRTL ? 'rtl' : 'ltr'}`}>
      <Link 
        to={`/video/${id}`}
        className="block w-full focus:outline-none"
      >
        <div className="relative w-full aspect-video rounded-xl overflow-hidden">
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />
          
          {badgeType && <Badge type={badgeType} />}

          {duration && broadcastStatus !== 'upcoming' && (
            <div className="absolute bottom-2 end-2 px-2 py-1 rounded-lg bg-black/80 backdrop-blur-sm text-white text-xs font-medium z-20">
              {formatDuration(duration)}
            </div>
          )}

          {getTimeDisplay && (
            <div className="absolute bottom-2 end-2 px-2 py-1 rounded-lg bg-black/80 backdrop-blur-sm text-white text-xs font-medium z-20">
              {getTimeDisplay.type === 'countdown' ? (
                <CountdownTimer startTime={scheduledStartTime!} />
              ) : (
                t('lives.startingTime', { time: getTimeDisplay.value })
              )}
            </div>
          )}

          <HoverPlayButton icon="ph:play-thin" />
        </div>

        <div className="mt-2 space-y-1">
          <h3 className="font-medium text-text-primary line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
            {title}
          </h3>
          
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            {views !== undefined && (
              <>
                <span>{t('video.views', { count: views })}</span>
                <span>•</span>
              </>
            )}
            <time dateTime={createdAt.toString()}>
              {formatTimeAgo(createdAt, normalizeLanguageCode(i18n.language))}
            </time>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default VideoThumbnail; 